import React, { useState } from 'react';
import {
  Card, Select, Alert, Modal, Form, InputNumber, Input, DatePicker, message, Empty, Space, Tabs,
} from 'antd';
import { TrophyOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { searchCompetitionPayStudents, collectCompetitionInstallment, getStudentCompetitions } from '../api/competitions';
import StudentCompetitionsPanel from '../components/competitions/StudentCompetitionsPanel';
import CompetitionCollectionsTab from '../components/competitions/CompetitionCollectionsTab';
import useDebounce from '../hooks/useDebounce';
import apiErrorMessage from '../utils/apiError';
import useAuthStore from '../store/authStore';
import { roleLevel } from '../utils/roleLevel';

/**
 * BANKODA YARIŞMA ÖDEMESİ.
 *
 * Öğretmenler bankoda normal ders ödemesi alıyor; yarışma ödemeleri ayrı.
 * Ama öğretmen hangi öğrencinin hangi yarışmada, hangi etapta, hangi
 * takımda olduğunu BİLMİYOR — bu yüzden ekran öğrenci odaklı: önce
 * öğrenci aranıyor, sonra o öğrencinin yarışma borcu listeleniyor.
 *
 * Yarışma modülünün kendi ekranları (etap → katılımcı) bu iş akışına
 * uymuyor; oraya bakmak için `competitions.view` gerekiyor ve o izin
 * öğretmende yok. Bu sayfa `competitions.payments` ile çalışıyor.
 *
 * ⚠ YÖNETİCİ OLMAYAN YALNIZCA BUGÜNE girebilir. Buradaki tarih kilidi
 * yalnızca GÖRÜNÜM; asıl kapı sunucuda (`collect` ucu 422 + `sebep`
 * döndürüyor). Arayüzde gizlemek yetki değildir.
 */
const CompetitionPayments = () => {
  const { user } = useAuthStore();
  // Yonetici mi? Role DEGIL seviyeye bakiliyor - sunucudaki kuralin aynisi
  // (roleRank >= branch_manager). Bu yalnizca GORUNUM; asil kapi sunucuda.
  const yonetici = (roleLevel[user?.role] ?? 0) >= roleLevel.branch_manager;

  const [arama, setArama] = useState('');
  const [secenekler, setSecenekler] = useState([]);
  const [araniyor, setAraniyor] = useState(false);
  const [ogrenci, setOgrenci] = useState(null);
  const [yenile, setYenile] = useState(0);

  const [modal, setModal] = useState(null);   // { id, label, currency, remaining }
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [form] = Form.useForm();

  // 2+ harfte sunucu tarafi arama (projenin select kurali). Debounce
  // olmadan her tusa bir istek giderdi.
  const gecikmeliArama = useDebounce(arama, 350);

  React.useEffect(() => {
    let iptal = false;
    const calistir = async () => {
      if (gecikmeliArama.trim().length < 2) { setSecenekler([]); return; }
      setAraniyor(true);
      try {
        const { data } = await searchCompetitionPayStudents(gecikmeliArama.trim());
        if (!iptal) {
          setSecenekler((data.data || []).map((o) => ({
            value: o.id,
            label: `${o.name} ${o.surname}`,
          })));
        }
      } catch (e) {
        if (!iptal) message.error(apiErrorMessage(e, 'Arama yapılamadı.'));
      } finally {
        if (!iptal) setAraniyor(false);
      }
    };
    calistir();
    return () => { iptal = true; };
  }, [gecikmeliArama]);

  const tahsilatAc = (taksit) => {
    setModal(taksit);
    form.setFieldsValue({
      paid_amount: taksit.remaining,
      payment_method: 'nakit',
      paid_at: dayjs(),
      notes: '',
    });
  };

  const tahsilatKaydet = async () => {
    const v = await form.validateFields();
    setKaydediliyor(true);
    try {
      const { data } = await collectCompetitionInstallment(modal.id, {
        paid_amount: v.paid_amount,
        payment_method: v.payment_method || null,
        // Tarih .format('YYYY-MM-DD') ile gonderilir - ham dayjs nesnesi
        // UTC'ye cevrilirken bir gun geriye kayar (CLAUDE.md).
        paid_at: v.paid_at ? v.paid_at.format('YYYY-MM-DD') : undefined,
        notes: v.notes || null,
      });
      message.success(data.message);
      setModal(null);
      setYenile((n) => n + 1);
    } catch (e) {
      message.error(apiErrorMessage(e, 'Tahsilat kaydedilemedi.'));
    } finally {
      setKaydediliyor(false);
    }
  };

  // Banko akisi (ogrenci ara -> borcunu listele -> tahsil et). Sekme
  // icerigi olarak ayri bir degiskende duruyor; Modal sekmenin DISINDA
  // kalsin diye (sekme degisince kapanmasin).
  const banko = (
    <div className="space-y-4">
      {!yonetici && (
        <Alert
          type="info"
          showIcon
          message="Yalnızca bugünün tahsilatı"
          description="Geçmiş tarihe tahsilat girilemez. Geçmişi düzeltmek için yöneticinize başvurun."
        />
      )}

      <Card size="small">
        <Space orientation="vertical" style={{ width: "100%" }}>
          <span className="text-sm text-gray-500">
            Öğrenci adının en az 2 harfini yazın. Yalnızca yarışmaya kayıtlı öğrenciler listelenir.
          </span>
          <Select
            showSearch
            allowClear
            style={{ width: '100%', maxWidth: 420 }}
            placeholder="Öğrenci ara"
            value={ogrenci?.value}
            loading={araniyor}
            // Sunucu tarafi arama: istemci filtrelemesi KAPATILMALI, yoksa
            // sunucudan gelen sonuclari bir de yerelde eleyip bos liste
            // gosterir.
            filterOption={false}
            onSearch={setArama}
            onChange={(v, opt) => setOgrenci(v ? { value: v, label: opt.label } : null)}
            options={secenekler}
            notFoundContent={
              arama.trim().length < 2 ? 'En az 2 harf yazın' : (araniyor ? 'Aranıyor…' : 'Sonuç yok')
            }
          />
        </Space>
      </Card>

      {ogrenci ? (
        <StudentCompetitionsPanel
          studentId={ogrenci.value}
          onTahsilat={tahsilatAc}
          yenileAnahtari={yenile}
        />
      ) : (
        <Empty description="Tahsilat için önce öğrenci arayın." image={Empty.PRESENTED_IMAGE_SIMPLE} />
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold mb-0">
        <TrophyOutlined className="mr-2" />
        Yarışma Ödemeleri
      </h2>

      {/* Gun sonu listesi AYNI SAYFADA ikinci sekme: tahsilati alan kisi ile
          mutabakati yapan kisi ayni ekrandan calisiyor. Sekme ilk kez
          acildiginda istek atiyor - banko akisini yavaslatmasin. */}
      <Tabs
        defaultActiveKey="banko"
        items={[
          { key: 'banko', label: 'Tahsilat', children: banko },
          { key: 'gun-sonu', label: 'Gün Sonu', children: <CompetitionCollectionsTab /> },
        ]}
      />

      <Modal
        title={modal ? `Tahsilat — ${modal.label || `${modal.no}. taksit`}` : ''}
        open={!!modal}
        onOk={tahsilatKaydet}
        confirmLoading={kaydediliyor}
        onCancel={() => setModal(null)}
        okText="Tahsilatı Kaydet"
        cancelText="Vazgeç"
        destroyOnHidden
      >
        {modal && (
          <>
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message={`Kalan borç: ${Number(modal.remaining).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ${modal.currency}`}
              description={`Tahsilat ${modal.currency} olarak kaydedilir — kur çevrilmez.`}
            />
            <Form form={form} layout="vertical">
              <Form.Item
                name="paid_amount"
                label={`Tutar (${modal.currency})`}
                rules={[{ required: true, message: 'Tutar girin.' }]}
              >
                <InputNumber min={0.01} max={modal.remaining} step={0.01} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="payment_method" label="Ödeme Yöntemi">
                <Select
                  options={[
                    { value: 'nakit', label: 'Nakit' },
                    { value: 'havale', label: 'Havale/EFT' },
                    { value: 'kart', label: 'Kart' },
                    { value: 'diger', label: 'Diğer' },
                  ]}
                />
              </Form.Item>
              <Form.Item name="paid_at" label="Tahsilat Tarihi">
                {/* Yonetici olmayanda alan KILITLI ve bugune sabit.
                    Bu yalnizca gorunum; asil kapi sunucuda. */}
                <DatePicker
                  style={{ width: '100%' }}
                  format="DD.MM.YYYY"
                  disabled={!yonetici}
                  allowClear={false}
                />
              </Form.Item>
              <Form.Item name="notes" label="Not">
                <Input.TextArea rows={2} maxLength={500} />
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>
    </div>
  );
};

export default CompetitionPayments;
